/** Mirrors CoverageStartDateAction.MAX_DAYS_IN_FUTURE, which validates the save. */
export const MAX_COVERAGE_START_DAYS = 29

/** Said up front, so neither a customer nor an agent applying for them finds the limit by failing it. */
export const COVERAGE_START_HINT = `Coverage can start today or any day in the next ${MAX_COVERAGE_START_DAYS} days.`

export enum CoverageType {
  AUTO_LIABILITY = 'AUTO_LIABILITY',
  AUTO_PHYSICAL_DAMAGE = 'AUTO_PHYSICAL_DAMAGE',
  GENERAL_LIABILITY = 'GENERAL_LIABILITY',
  MOTOR_TRUCK_CARGO = 'MOTOR_TRUCK_CARGO',
  WORKERS_COMPENSATION = 'WORKERS_COMPENSATION',
  /** Offered only to a confirmed owner-operator, through the application's coverage menu. */
  PET = 'PET',
  TOOLS_AND_EQUIPMENT = 'TOOLS_AND_EQUIPMENT',
}

/** Mirrors InsuranceCoverage.COVERAGE_TYPE_LABEL_MAP on the backend. */
export const COVERAGE_TYPE_LABELS: Record<CoverageType, string> = {
  [CoverageType.AUTO_LIABILITY]: 'Auto Liability',
  [CoverageType.AUTO_PHYSICAL_DAMAGE]: 'Physical Damage',
  [CoverageType.GENERAL_LIABILITY]: 'General Liability',
  [CoverageType.MOTOR_TRUCK_CARGO]: 'Cargo',
  [CoverageType.WORKERS_COMPENSATION]: 'Workers Comp',
  [CoverageType.PET]: 'Pet insurance',
  [CoverageType.TOOLS_AND_EQUIPMENT]: 'Tools and Equipment',
}

export interface InsuranceCoverage {
  id: string
  dateCreated: string
  dateUpdated: string
  coverageType: CoverageType
  limit: number
  deductible: number
  comprehensiveDeductible?: number | null
}

export interface EnabledCoverage {
  coverageType: CoverageType
  label: string
  description: string
  required: boolean
}

/**
 * Mirrors InsuranceCoverage.ENABLED_COVERAGE_TYPES on the backend, which is also what
 * validates a submission — auto liability is the one that can't be dropped. The chat's
 * coverage picker is served this list by the backend; the pickers outside the chat use
 * this copy.
 */
export const ENABLED_COVERAGES: ReadonlyArray<EnabledCoverage> = [
  {
    coverageType: CoverageType.AUTO_LIABILITY,
    label: COVERAGE_TYPE_LABELS[CoverageType.AUTO_LIABILITY],
    description:
      'Covers damage or injuries to others, including property damage, vehicle damage, and medical expenses.',
    required: true,
  },
  {
    coverageType: CoverageType.AUTO_PHYSICAL_DAMAGE,
    label: COVERAGE_TYPE_LABELS[CoverageType.AUTO_PHYSICAL_DAMAGE],
    description:
      'Covers damages to your truck from collisions or other incidents. A must-have for comprehensive protection.',
    required: false,
  },
  {
    coverageType: CoverageType.GENERAL_LIABILITY,
    label: COVERAGE_TYPE_LABELS[CoverageType.GENERAL_LIABILITY],
    description: 'Covers injuries or property damage related to your business (not related to your truck).',
    required: false,
  },
  {
    coverageType: CoverageType.MOTOR_TRUCK_CARGO,
    label: COVERAGE_TYPE_LABELS[CoverageType.MOTOR_TRUCK_CARGO],
    description:
      'Safeguards your hauled goods from loss or damage due to accidents, theft, or natural disasters.',
    required: false,
  },
]

/** The coverages a picker must start with ticked — they render disabled, so an unticked
 *  one can never be corrected and the backend rejects every save without it. */
export const REQUIRED_COVERAGES: ReadonlyArray<CoverageType> = ENABLED_COVERAGES.filter(
  (coverage) => coverage.required
).map((coverage) => coverage.coverageType)
